import * as React from 'react';

import { BoxProps } from '@leagueplatform/genesis-commons';
import { Box, queryHelpers, useMediaQuery } from '@leagueplatform/genesis-core';
import { getModulePath, LEAGUE_MODULE_NAMES } from '@leagueplatform/core';
import { useScrollTopOnLocationChange } from '@leagueplatform/web-common';
import {
  APP_NAV_DEFAULT_HEIGHT,
  APP_NAV_MOBILE_HEIGHT,
  AuthedWebviewModal,
  DraggableLiveChat,
  FEATURE_FLAGS,
  INTERNAL_BASE_ROUTES,
  LinkHandler,
  LoadingSpinner,
  QueryParamModal,
  useSsoDeepLink,
  ImpersonationMasonryBanner,
  ErrorBoundary,
} from '@highmark-web/highmark-common';
import { useLocation } from '@leagueplatform/routing';
import { useGetAppFeatureFlag } from 'hooks/use-get-app-feature-flag.hook';
import { AppNavBarComponent } from './nav.component';
import { BeneficityFooter } from './footer/beneficity-footer.component';

const ExcludeChatBubbleRoutes = [
  getModulePath(LEAGUE_MODULE_NAMES.healthJourney),
  getModulePath(LEAGUE_MODULE_NAMES.healthPrograms),
  getModulePath(LEAGUE_MODULE_NAMES.healthProgress),
  getModulePath(LEAGUE_MODULE_NAMES.healthJourneyHistory),
  INTERNAL_BASE_ROUTES.COORDINATION_OF_BENEFITS_BASE,
];

export const PrivateBasePage: React.FC<BoxProps> = ({
  enableLiveChat,
  children,
}) => {
  useScrollTopOnLocationChange();
  useSsoDeepLink(LinkHandler);

  const { isEnabled: isLiveChatNavigateAwayEnabled } = useGetAppFeatureFlag(
    FEATURE_FLAGS.LIVE_CHAT_NAVIGATE_AWAY,
  );

  const location = useLocation();
  // Get first path string before non character value (/, ?, etc)
  const basePath = location?.pathname?.match(/^\/[\w-]+/) ?? ['/'];
  const isChatExcludedPath = ExcludeChatBubbleRoutes.includes(basePath[0]);

  const isChatBubbleEnabled = Boolean(
    !isChatExcludedPath && enableLiveChat && isLiveChatNavigateAwayEnabled,
  );

  const isMobile = useMediaQuery(queryHelpers.down('mobileLandscape'));

  const MAIN_CONTENT_MIN_HEIGHT = `calc(100vh - ${
    isMobile ? APP_NAV_MOBILE_HEIGHT : APP_NAV_DEFAULT_HEIGHT
  }px)`;

  return (
    <>
      <AppNavBarComponent />
      <AuthedWebviewModal />
      <QueryParamModal />
      <ImpersonationMasonryBanner />
      <Box
        as="main"
        id="main-content"
        css={{
          position: 'relative',
          flex: '1',
          overflowX: 'hidden',
          // This is so any page will take up the whole screen regardless of content
          minHeight: MAIN_CONTENT_MIN_HEIGHT,
          paddingBottom: '$twoAndHalf',
        }}
      >
        <ErrorBoundary
          contextLocation="BasePage ErrorBoundary"
          styleErrorProps={{
            minHeight: MAIN_CONTENT_MIN_HEIGHT,
          }}
        >
          <React.Suspense fallback={<LoadingSpinner />}>
            {children}
          </React.Suspense>
        </ErrorBoundary>
      </Box>
      <BeneficityFooter />
      {isChatBubbleEnabled && <DraggableLiveChat />}
    </>
  );
};
